import { AttackPattern, PatternState } from '../AttackPattern.js';

let nextWaltzToken = 1;

const GAP_COUNT = 2; // opposing doorways through the blade ring
const GAP_HALF_WIDTH = 0.36; // radians — generous enough to walk, tight enough to time

/**
 * Waltz of Daggers (The Jealous Regent) — a turning ring of blades at
 * fixed radius around the Regent, with two opposing gaps that rotate with
 * it. It splits the arena into a hard inside/outside: cross only through a
 * doorway (or dash through the blades), and the Jealous Embrace is usually
 * dragging you toward the wrong side of it. At the final chord the whole
 * ring is flung outward — standing far away is exactly where the daggers
 * are going. Implemented as real pooled bullets (grazeable, eaten by
 * Resonant Pillars) steered onto their orbit each tick, not a painted-arc
 * zone — the Sanctum's rotating wedge is floor; this is a wall of shrapnel.
 */
export class WaltzOfDaggers extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Waltz of Daggers', telegraphDuration: 1.0, activeDuration: 5.6, minPhase });
    this.config = config;
  }

  onBegin(boss) {
    this._token = nextWaltzToken++;
    this._daggers = [];
    this._spin = Math.random() * Math.PI * 2;
    this._omega = this.config.orbitOmega * (Math.random() < 0.5 ? 1 : -1) * (1 + boss.phase * 0.12);
    this._flung = false;
  }

  _inGap(ringAngle) {
    for (let g = 0; g < GAP_COUNT; g++) {
      const gapCenter = (g / GAP_COUNT) * Math.PI * 2;
      let diff = ringAngle - gapCenter;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (Math.abs(diff) < GAP_HALF_WIDTH) return true;
    }
    return false;
  }

  onActiveStart(boss, _player, pool) {
    const count = this.config.daggerCount + boss.phase * 2;
    for (let i = 0; i < count; i++) {
      const ringAngle = (i / count) * Math.PI * 2;
      if (this._inGap(ringAngle)) continue; // the doorways stay empty
      const bullet = pool.fire({
        x: boss.x + Math.cos(this._spin + ringAngle) * this.config.orbitRadius,
        y: boss.y + Math.sin(this._spin + ringAngle) * this.config.orbitRadius,
        vx: 0,
        vy: 0,
        radius: 2.7,
        damage: this.config.damage,
        color: '#ff87b3',
        lifetime: this.activeDuration + 3, // survives the fling at the end
      });
      bullet._waltzToken = this._token;
      this._daggers.push({ bullet, ringAngle });
    }
  }

  onActiveTick(dt, boss, _player, _pool) {
    this._spin += this._omega * dt;

    // The last stretch is the fling — handled once, then the blades fly free.
    if (this._timer <= 0.9 && !this._flung) {
      this._fling(boss);
      return;
    }
    if (this._flung) return;

    // Velocity-steering onto next frame's orbit slot: exact under the fixed
    // timestep, and keeps prevX/prevY trails + graze checks working since
    // the daggers genuinely move rather than teleport.
    const nextSpin = this._spin + this._omega * dt;
    for (const { bullet, ringAngle } of this._daggers) {
      if (!bullet.active || bullet._waltzToken !== this._token) continue;
      if (bullet._grandPaused) continue;
      const tx = boss.x + Math.cos(nextSpin + ringAngle) * this.config.orbitRadius;
      const ty = boss.y + Math.sin(nextSpin + ringAngle) * this.config.orbitRadius;
      bullet.vx = (tx - bullet.x) / dt;
      bullet.vy = (ty - bullet.y) / dt;
      bullet.speed = Math.hypot(bullet.vx, bullet.vy);
    }
  }

  _fling(boss) {
    this._flung = true;
    for (const { bullet, ringAngle } of this._daggers) {
      if (!bullet.active || bullet._waltzToken !== this._token) continue;
      bullet._waltzToken = null;
      const outAngle = this._spin + ringAngle;
      bullet.vx = Math.cos(outAngle) * this.config.flingSpeed;
      bullet.vy = Math.sin(outAngle) * this.config.flingSpeed;
      bullet.speed = this.config.flingSpeed;
    }
    boss.eventBus?.emit('waltz:fling');
  }

  onEnd(_boss) {
    // Anything not yet flung (config shortened the window) unpins cleanly.
    for (const { bullet } of this._daggers) {
      if (bullet._waltzToken === this._token) bullet._waltzToken = null;
    }
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return {
        kind: 'waltzRing',
        x: boss.x,
        y: boss.y,
        radius: this.config.orbitRadius,
        t: 1 - this._timer / this.telegraphDuration,
        spin: this._spin,
        gapHalf: GAP_HALF_WIDTH,
        gapCount: GAP_COUNT,
      };
    }
    return null; // live daggers ARE the visual once the waltz begins
  }
}
